import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useProBranding } from "@/hooks/use-pro-branding";
import { generateBrandedPdf } from "@/lib/pdfBranded";
import { saveDoc } from "@/lib/localHistory";
import { FileText, Download, Sparkles, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const Route = createFileRoute("/ferramentas/contratos-pdf")({
  head: () => ({
    meta: [
      { title: "Gerador de contratos em PDF — MEI Toolbox" },
      {
        name: "description",
        content:
          "Gerador grátis de contratos de prestação de serviço e recibos profissionais em PDF para MEI: prontos para assinar, com a logo, CNPJ e dados da sua empresa.",
      },
      { property: "og:title", content: "Gerador de contratos em PDF — MEI Toolbox" },
      {
        property: "og:description",
        content:
          "Gere contratos de prestação de serviço em PDF com a logo e os dados da sua empresa.",
      },
      { property: "og:url", content: "https://meitoolbox.com.br/ferramentas/contratos-pdf" },
    ],
    links: [{ rel: "canonical", href: "https://meitoolbox.com.br/ferramentas/contratos-pdf" }],
  }),
  component: ContractsPage,
});

function ContractsPage() {
  const { branding, loading: brandLoading } = useProBranding();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    contratante: "",
    contratanteDoc: "",
    contratado: "",
    contratadoDoc: "",
    servico: "",
    valor: "",
    prazo: "",
    cidade: "",
  });

  const update =
    (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

  const hasBrand = !!(branding?.company_name || branding?.logo_url);

  const generate = async () => {
    if (!form.contratante || !form.contratado || !form.servico) {
      toast.error("Preencha pelo menos contratante, contratado e descrição do serviço.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        title: "Contrato de Prestação de Serviços",
        intro: `Pelo presente instrumento particular, as partes abaixo qualificadas têm entre si justo e contratado o seguinte:`,
        fileName: `contrato-${(form.contratante || "cliente").toLowerCase().replace(/\s+/g, "-").slice(0, 30)}`,
        sections: [
          {
            title: "Partes",
            rows: [
              { label: "Contratante", value: form.contratante },
              { label: "CPF/CNPJ contratante", value: form.contratanteDoc || "—" },
              { label: "Contratado", value: form.contratado },
              { label: "CPF/CNPJ contratado", value: form.contratadoDoc || "—" },
            ],
          },
          {
            title: "Cláusula 1ª — Objeto",
            rows: [{ label: "Serviço", value: form.servico }],
          },
          {
            title: "Cláusula 2ª — Valor e pagamento",
            rows: [
              { label: "Valor total", value: form.valor ? `R$ ${form.valor}` : "A combinar" },
              { label: "Forma", value: "Conforme acordado entre as partes" },
            ],
          },
          {
            title: "Cláusula 3ª — Prazo",
            rows: [{ label: "Execução", value: form.prazo || "A combinar" }],
          },
          {
            title: "Cláusula 4ª — Foro",
            rows: [{ label: "Foro eleito", value: form.cidade || "A combinar" }],
          },
          {
            title: "Assinaturas",
            rows: [
              {
                label: "Local e data",
                value: `${form.cidade || "____________"}, ${new Date().toLocaleDateString("pt-BR")}`,
              },
              { label: "_______________________", value: "CONTRATANTE" },
              { label: "_______________________", value: "CONTRATADO" },
            ],
          },
        ],
        notes:
          "Documento gerado automaticamente. Recomenda-se revisão por profissional de Direito antes da assinatura.",
      };

      await generateBrandedPdf({ ...payload, branding });

      saveDoc({
        tool: "Contrato de prestação de serviços",
        title: `Contrato — ${form.contratante || "sem nome"}`,
        file_name: payload.fileName,
        payload,
      });
      toast.success("Contrato gerado e salvo no seu histórico (neste navegador).");
    } catch (e) {
      console.error(e);
      toast.error("Não foi possível gerar o contrato");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="container mx-auto max-w-3xl px-4 py-12">
      <div className="flex items-center gap-2 text-amber-600">
        <FileText className="h-5 w-5" /> <span className="text-xs font-bold uppercase">Grátis</span>
      </div>
      <h1 className="mt-2 text-3xl font-bold">Gerador de contratos em PDF</h1>
      <p className="mt-2 text-muted-foreground">
        Preencha os dados, gere e baixe um contrato de prestação de serviço — com a logo e os dados
        da sua empresa no cabeçalho.
      </p>

      {!brandLoading &&
        (hasBrand ? (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/20 p-4 flex items-start gap-3">
            <Sparkles className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-emerald-900 dark:text-emerald-100">
                Sua marca está configurada
                {branding?.company_name && (
                  <>
                    {" "}
                    — <strong>{branding.company_name}</strong>
                  </>
                )}
              </p>
              <p className="text-emerald-700 dark:text-emerald-300 text-xs mt-0.5">
                {branding?.logo_url ? "Logo + " : ""}dados da empresa serão impressos no topo do
                contrato.{" "}
                <Link to="/marca" className="underline font-semibold">
                  Editar marca
                </Link>
              </p>
            </div>
          </div>
        ) : (
          <div className="mt-6 rounded-2xl border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1 text-sm">
              <p className="font-semibold text-amber-900 dark:text-amber-100">
                Adicione sua marca antes de gerar
              </p>
              <p className="text-amber-700 dark:text-amber-300 text-xs mt-0.5">
                Faça upload da sua logo e preencha CNPJ, telefone e endereço para que apareçam em
                todos os contratos.
              </p>
              <Link
                to="/marca"
                className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-600 px-3 py-1.5 text-xs font-bold text-white"
              >
                Configurar minha marca →
              </Link>
            </div>
          </div>
        ))}

      <div className="mt-8 grid md:grid-cols-2 gap-4">
        <Field
          label="Nome do contratante (cliente)"
          value={form.contratante}
          onChange={update("contratante")}
        />
        <Field
          label="CPF/CNPJ do contratante"
          value={form.contratanteDoc}
          onChange={update("contratanteDoc")}
        />
        <Field
          label="Nome do contratado (você)"
          value={form.contratado}
          onChange={update("contratado")}
        />
        <Field
          label="CPF/CNPJ do contratado"
          value={form.contratadoDoc}
          onChange={update("contratadoDoc")}
        />
        <div className="md:col-span-2">
          <Field
            label="Descrição do serviço"
            value={form.servico}
            onChange={update("servico")}
            textarea
          />
        </div>
        <Field label="Valor total (R$)" value={form.valor} onChange={update("valor")} />
        <Field label="Prazo (ex: 30 dias)" value={form.prazo} onChange={update("prazo")} />
        <div className="md:col-span-2">
          <Field label="Cidade / UF" value={form.cidade} onChange={update("cidade")} />
        </div>
      </div>

      <button
        onClick={generate}
        disabled={busy}
        className="mt-8 inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-600 px-6 py-3 text-sm font-bold text-white disabled:opacity-60"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        Gerar contrato em PDF
      </button>

      <article className="mt-16 prose prose-sm max-w-none dark:prose-invert">
        <h2>Gerador grátis de contrato de prestação de serviço para MEI</h2>
        <p>
          O contrato de prestação de serviços é o documento que formaliza o acordo entre o MEI e o
          cliente, protegendo as duas partes contra mal-entendidos sobre prazo, escopo e pagamento.
          Mesmo um trabalho simples — instalação, consultoria, manutenção, design, aulas
          particulares — fica mais profissional quando há um contrato assinado. Com esta ferramenta
          gratuita do MEI Toolbox, você gera um modelo completo em PDF em menos de um minuto, sem
          precisar de software pago ou modelo de Word baixado da internet.
        </p>

        <h3>O que entra no contrato gerado</h3>
        <p>
          O PDF criado inclui qualificação das partes (contratante e contratado, com CPF ou CNPJ),
          descrição detalhada do serviço, valor total combinado, prazo de execução, foro eleito para
          resolver disputas e os campos de assinatura. Se você configurou sua{" "}
          <Link to="/marca">marca no MEI Toolbox</Link>, o logotipo, CNPJ, telefone e endereço da
          sua empresa aparecem no cabeçalho — o que dá identidade visual profissional ao documento e
          aumenta a credibilidade frente ao cliente.
        </p>

        <h3>Quando o MEI precisa de contrato?</h3>
        <p>
          Sempre que o serviço envolve prazo, parcelas, materiais fornecidos pelo cliente ou
          expectativa de entrega futura. Para vendas avulsas e atendimentos rápidos, a nota fiscal
          eletrônica costuma ser suficiente; mas para reformas, projetos, mensalidades, consultorias
          e qualquer trabalho com mais de uma entrega, o contrato evita disputas. A Receita Federal
          não exige o contrato para fins de tributação do Simples Nacional, mas ele é prova em juízo
          e em demandas no Procon.
        </p>

        <h3>Como usar este modelo</h3>
        <ol>
          <li>Preencha os dados do cliente (contratante) e os seus dados (contratado).</li>
          <li>
            Descreva o serviço de forma objetiva — quanto mais claro, menos margem para
            discordância.
          </li>
          <li>Defina o valor total e o prazo; deixe "a combinar" se ainda estiver negociando.</li>
          <li>Informe a cidade — ela é usada também como foro eleito para o contrato.</li>
          <li>
            Clique em "Gerar contrato em PDF", imprima duas vias e colete as assinaturas das duas
            partes.
          </li>
        </ol>

        <h3>Dicas para um contrato mais seguro</h3>
        <p>
          Sempre que possível, anexe ao contrato uma proposta comercial mais detalhada (com
          cronograma, materiais, condições de pagamento e política de cancelamento). Guarde a via
          assinada por pelo menos cinco anos — é o prazo prescricional para a maioria das cobranças
          cíveis no Brasil. Para serviços recorrentes (mensalidades), inclua uma cláusula de
          reajuste anual pelo IPCA. Para trabalhos de grande valor, recomenda-se revisão por
          advogado.
        </p>

        <h3>Privacidade dos seus dados</h3>
        <p>
          Tudo o que você digita aqui é processado no próprio navegador e o PDF é gerado localmente
          — nada é enviado para servidores do MEI Toolbox. Quando você gera o documento, ele é salvo
          automaticamente no <Link to="/historico">seu histórico local</Link>, disponível neste
          mesmo navegador para baixar de novo quando quiser.
        </p>
      </article>
    </section>
  );
}

function Field({
  label,
  value,
  onChange,
  textarea,
}: {
  label: string;
  value: string;
  onChange: (e: any) => void;
  textarea?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-sm font-semibold">{label}</span>
      {textarea ? (
        <textarea
          value={value}
          onChange={onChange}
          rows={3}
          className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
        />
      ) : (
        <input
          value={value}
          onChange={onChange}
          className="mt-1 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
        />
      )}
    </label>
  );
}
